import { DBSchema, IDBPDatabase, openDB } from "idb";
import { Album, Playlist, RadioStation, Track } from "../types";
import { TrackAnalysis } from "../hooks/useHapticsEngine/types"; // Import TrackAnalysis

interface SpotifyCloneDB extends DBSchema {
  localFiles: {
    key: string;
    value: Track & { blobData?: ArrayBuffer };
    indexes: {
      "by-title": string;
      "by-artist": string;
    };
  };
  playlists: {
    key: string;
    value: Playlist;
    indexes: {
      "by-name": string;
    };
  };
  radioStations: {
    key: string;
    value: RadioStation;
    indexes: {
      "by-name": string;
    };
  };
  likedContent: {
    key: string;
    value: {
      id: string;
      type: "track" | "album" | "playlist" | "radio";
      source: "local" | "spotify";
      timestamp: number;
    };
    indexes: {
      "by-type": string;
      "by-timestamp": number;
    };
  };
  recentlyPlayed: {
    key: string;
    value: Track & { timestamp: number };
    indexes: {
      "by-timestamp": number;
    };
  };
  stats: {
    key: string;
    value: {
      playHistory: any[];
      sessions: any[];
      totalPlayTime: number;
      streaks: {
        current: number;
        longest: number;
        lastPlayDate: string;
      };
    };
  };
  settings: {
    key: string;
    value: {
      equalizer: {
        bands: number[];
        enabled: boolean;
      };
      crossfade: number;
      normalization: boolean;
      currentPreset: string;
    };
  };
  localLibrary: {
    key: string;
    value: any;
  };
  albums: {
    key: string;
    value: Album;
    indexes: {
      "by-name": string;
      "by-artist": string;
    };
  };
  profile: {
    key: string;
    value: {
      name: string;
      avatar?: string;
      theme: "dark" | "darker";
      accentColor: string;
      showListeningStatus: boolean;
      shareProfile: boolean;
      emailNotifications: boolean;
      pushNotifications: boolean;
      email?: string;
      memberSince?: string;
      developerMode: boolean;
      developerModeTaps: number;
      wasDevBefore: boolean;
    };
  };
  hapticAnalyses: { // New object store for haptic analyses
    key: string; // trackId
    value: TrackAnalysis;
  };
}

export const DB_NAME = "pulse-player-db";
const DB_VERSION = 8; // Incremented DB_VERSION

let dbPromise: Promise<IDBPDatabase<SpotifyCloneDB>> | null = null;

const getDB = () => {
  if (!dbPromise) {
    dbPromise = openDB<SpotifyCloneDB>(DB_NAME, DB_VERSION, {
      async upgrade(db, oldVersion, newVersion) {
        // Create stores if they don't exist
        if (!db.objectStoreNames.contains("localFiles")) {
          const localFilesStore = db.createObjectStore("localFiles", { keyPath: "id" });
          localFilesStore.createIndex("by-title", "title");
          localFilesStore.createIndex("by-artist", "artist");
        }

        if (!db.objectStoreNames.contains("albums")) {
          const albumsStore = db.createObjectStore("albums", { keyPath: "id" });
          albumsStore.createIndex("by-name", "name");
          albumsStore.createIndex("by-artist", "artist");
        }

        if (!db.objectStoreNames.contains("playlists")) {
          const playlistsStore = db.createObjectStore("playlists", { keyPath: "id" });
          playlistsStore.createIndex("by-name", "name");
        }

        if (!db.objectStoreNames.contains("radioStations")) {
          const radioStationsStore = db.createObjectStore("radioStations", { keyPath: "id" });
          radioStationsStore.createIndex("by-name", "name");
        }

        if (!db.objectStoreNames.contains("likedContent")) {
          const likedContentStore = db.createObjectStore("likedContent", { keyPath: "id" });
          likedContentStore.createIndex("by-type", "type");
          likedContentStore.createIndex("by-timestamp", "timestamp");
        }

        if (!db.objectStoreNames.contains("recentlyPlayed")) {
          const recentlyPlayedStore = db.createObjectStore("recentlyPlayed", { keyPath: "id" });
          recentlyPlayedStore.createIndex("by-timestamp", "timestamp");
        }

        if (!db.objectStoreNames.contains("stats")) {
          db.createObjectStore("stats", { keyPath: "id" });
        }

        if (!db.objectStoreNames.contains("settings")) {
          db.createObjectStore("settings", { keyPath: "id" });
        }

        if (!db.objectStoreNames.contains("localLibrary")) {
          db.createObjectStore("localLibrary", { keyPath: "id" });
        }

        if (!db.objectStoreNames.contains("profile")) {
          db.createObjectStore("profile", { keyPath: "id" });
        }

        // Add hapticAnalyses store if it doesn't exist
        if (!db.objectStoreNames.contains("hapticAnalyses")) {
          db.createObjectStore("hapticAnalyses", { keyPath: "trackId" });
        }

        // Handle data migration
        if (oldVersion < 6 && db.objectStoreNames.contains("playlists")) { // Example existing migration
          try {
            const playlistTx = db.transaction("playlists", "readonly");
            const playlistStore = playlistTx.objectStore("playlists");
            const items = await playlistStore.getAll();

            // Wait for the transaction to complete
            await playlistTx.done;

            // Start a new transaction for writing albums
            const albumTx = db.transaction("albums", "readwrite");
            const albumStore = albumTx.objectStore("albums");

            // Filter albums and migrate them
            const albums = items.filter((item) => item.isAlbum);
            for (const album of albums) {
              const { isAlbum, ...cleanAlbum } = album;
              await albumStore.put(cleanAlbum as Album);
            }

            // Wait for album transaction to complete
            await albumTx.done;

            // Clean up old album entries from playlists
            const cleanupTx = db.transaction("playlists", "readwrite");
            const cleanupStore = cleanupTx.objectStore("playlists");
            for (const album of albums) {
              await cleanupStore.delete(album.id);
            }
            await cleanupTx.done;
          } catch (error) {
            console.error("Error during migration:", error);
          }
        }
      },
      blocked(currentVersion, blockedVersion, event) {
        console.warn("Database upgrade was blocked", { currentVersion, blockedVersion, event });
      },
      blocking(currentVersion, blockedVersion, event) {
        console.warn("Database is blocking an upgrade", { currentVersion, blockedVersion, event });
      },
      terminated() {
        console.error("Database connection was terminated");
        dbPromise = null;
      },
    });
  }
  return dbPromise;
};

export const dbHelpers = {
  async addTrack(track: Track & { blobData?: ArrayBuffer }): Promise<void> {
    const db = await getDB();
    await db.put("localFiles", track);
  },

  async getTrack(id: string): Promise<(Track & { blobData?: ArrayBuffer }) | undefined> {
    const db = await getDB();
    return db.get("localFiles", id);
  },

  async getAllTracks(): Promise<(Track & { blobData?: ArrayBuffer })[]> {
    const db = await getDB();
    return db.getAll("localFiles");
  },

  async deleteTrack(id: string): Promise<void> {
    const db = await getDB();
    await db.delete("localFiles", id);
  },

  async addPlaylist(playlist: Playlist): Promise<void> {
    const db = await getDB();
    await db.put("playlists", playlist);
  },

  async getPlaylist(id: string): Promise<Playlist | undefined> {
    const db = await getDB();
    return db.get("playlists", id);
  },

  async getAllPlaylists(): Promise<Playlist[]> {
    const db = await getDB();
    return db.getAll("playlists");
  },

  async deletePlaylist(id: string): Promise<void> {
    const db = await getDB();
    await db.delete("playlists", id);
  },

  async addRadioStation(station: RadioStation): Promise<void> {
    const db = await getDB();
    await db.put("radioStations", station);
  },

  async getRadioStation(id: string): Promise<RadioStation | undefined> {
    const db = await getDB();
    return db.get("radioStations", id);
  },

  async getAllRadioStations(): Promise<RadioStation[]> {
    const db = await getDB();
    return db.getAll("radioStations");
  },

  async deleteRadioStation(id: string): Promise<void> {
    const db = await getDB();
    await db.delete("radioStations", id);
  },

  async getLikedContent(): Promise<any[]> {
    const db = await getDB();
    return db.getAllFromIndex("likedContent", "by-timestamp");
  },

  async addLikedContent(content: any): Promise<void> {
    const db = await getDB();
    await db.put("likedContent", content);
  },

  async removeLikedContent(id: string): Promise<void> {
    const db = await getDB();
    await db.delete("likedContent", id);
  },

  async getRecentlyPlayed(): Promise<Track[]> {
    const db = await getDB();
    const tracks = await db.getAllFromIndex("recentlyPlayed", "by-timestamp");
    return tracks.sort((a, b) => b.timestamp - a.timestamp);
  },

  async addRecentlyPlayed(track: Track): Promise<void> {
    const db = await getDB();
    await db.put("recentlyPlayed", {
      ...track,
      timestamp: Date.now(),
    });
  },

  async getStats(): Promise<any> {
    const db = await getDB();
    return db.get("stats", "user-stats");
  },

  async updateStats(stats: any): Promise<void> {
    const db = await getDB();
    await db.put("stats", { ...stats, id: "user-stats" });
  },

  async getSettings(): Promise<any> {
    const db = await getDB();
    return db.get("settings", "user-settings");
  },

  async updateSettings(settings: any): Promise<void> {
    const db = await getDB();
    await db.put("settings", { ...settings, id: "user-settings" });
  },

  async clearDatabase(): Promise<void> {
    const db = await getDB();
    await Promise.all([
      db.clear("localFiles"),
      db.clear("playlists"),
      db.clear("radioStations"),
      db.clear("likedContent"),
      db.clear("recentlyPlayed"),
      db.clear("stats"),
      db.clear("settings"),
    ]);
  },

  async saveLocalLibraryState(state: any): Promise<void> {
    const db = await getDB();
    await db.put("localLibrary", { id: "state", ...state });
  },

  async loadLocalLibraryState(): Promise<any> {
    const db = await getDB();
    return db.get("localLibrary", "state");
  },

  // Add Album helper
  async addAlbum(album: Album): Promise<void> {
    const db = await getDB();
    await db.put("albums", album);
  },

  async getAlbum(id: string): Promise<Album | undefined> {
    const db = await getDB();
    return db.get("albums", id);
  },

  async getAllAlbums(): Promise<Album[]> {
    const db = await getDB();
    return db.getAll("albums");
  },

  async deleteAlbum(id: string): Promise<void> {
    const db = await getDB();
    await db.delete("albums", id);
  },

  // Profile helpers
  async getProfile(): Promise<any> {
    const db = await getDB();
    return db.get("profile", "user-profile");
  },

  async updateProfile(profile: any): Promise<void> {
    const db = await getDB();
    await db.put("profile", { ...profile, id: "user-profile" });
  },

  // Helper functions for hapticAnalyses
  async saveHapticAnalysis(analysis: TrackAnalysis): Promise<void> {
    const db = await getDB();
    await db.put("hapticAnalyses", analysis);
  },

  async getHapticAnalysis(trackId: string): Promise<TrackAnalysis | undefined> {
    const db = await getDB();
    return db.get("hapticAnalyses", trackId);
  },

  async deleteHapticAnalysis(trackId: string): Promise<void> {
    const db = await getDB();
    await db.delete("hapticAnalyses", trackId);
  }
};
